import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Separator } from '../components/ui/separator';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { 
  Wallet, 
  PiggyBank, 
  LogOut, 
  ArrowUpRight, 
  ArrowDownRight,
  CreditCard,
  Building2,
  Info,
  Send,
  TrendingUp,
  Clock,
  X
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const UserDashboard = () => {
  const { user, logout, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [wireTransferModal, setWireTransferModal] = useState(false);
  const [investmentModal, setInvestmentModal] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const headers = getAuthHeaders();
      const [accountsRes, transactionsRes] = await Promise.all([
        axios.get(`${API}/user/accounts`, { headers, withCredentials: true }),
        axios.get(`${API}/user/transactions`, { headers, withCredentials: true })
      ]);
      setAccounts(accountsRes.data);
      setTransactions(transactionsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const spendAccount = accounts.find(a => a.account_type === 'Spend');
  const holdAccount = accounts.find(a => a.account_type === 'Hold');
  const treasuryAccount = accounts.find(a => a.account_type === 'Treasury');

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getAccountTransactions = (accountId) => {
    return transactions.filter(t => t.account_id === accountId);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="user-dashboard">
      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-full bg-accent/20 flex items-center justify-center border border-accent/30">
              <CreditCard className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-heading text-xl font-semibold tracking-tight" data-testid="user-code">
                {user?.internal_code}
              </h1>
              <p className="text-sm text-muted-foreground">Account Dashboard</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button 
              variant="outline" 
              onClick={() => setWireTransferModal(true)}
              className="border-border hover:bg-secondary"
              data-testid="wire-transfer-btn"
            >
              <Send className="w-4 h-4 mr-2" />
              Initiate Wire Transfer
            </Button>
            <Button 
              variant="outline" 
              onClick={() => setInvestmentModal(true)}
              className="border-border hover:bg-secondary"
              data-testid="investment-btn"
            >
              <TrendingUp className="w-4 h-4 mr-2" />
              Investment Dashboard
            </Button>
            <Button 
              variant="outline" 
              onClick={handleLogout}
              className="border-border hover:bg-secondary"
              data-testid="logout-button"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sign Out
            </Button>
          </div>
        </div>
      </header>

      {/* Wire Transfer Coming Soon Modal */}
      <Dialog open={wireTransferModal} onOpenChange={setWireTransferModal}>
        <DialogContent className="bg-card border-border sm:max-w-md">
          <DialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 rounded-full bg-accent/20 flex items-center justify-center">
                <Clock className="w-6 h-6 text-blue-400" />
              </div>
              <DialogTitle className="font-heading text-xl">Initiate Wire Transfer</DialogTitle>
            </div>
          </DialogHeader>
          <div className="py-4">
            <p className="text-muted-foreground text-center">
              Wire transfer functionality is coming soon.
            </p>
            <p className="text-sm text-muted-foreground/70 text-center mt-2">
              This feature is not yet available.
            </p>
          </div>
          <DialogFooter>
            <Button 
              onClick={() => setWireTransferModal(false)}
              className="w-full bg-white text-[#0A0F1C] hover:bg-white/90"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Investment Dashboard Coming Soon Modal */}
      <Dialog open={investmentModal} onOpenChange={setInvestmentModal}>
        <DialogContent className="bg-card border-border sm:max-w-md">
          <DialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 rounded-full bg-accent/20 flex items-center justify-center">
                <Clock className="w-6 h-6 text-blue-400" />
              </div>
              <DialogTitle className="font-heading text-xl">Investment Dashboard</DialogTitle>
            </div>
          </DialogHeader>
          <div className="py-4">
            <p className="text-muted-foreground text-center">
              Investment dashboard functionality is coming soon.
            </p>
            <p className="text-sm text-muted-foreground/70 text-center mt-2">
              This feature is not yet available.
            </p>
          </div>
          <DialogFooter>
            <Button 
              onClick={() => setInvestmentModal(false)}
              className="w-full bg-white text-[#0A0F1C] hover:bg-white/90"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Balance Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* Spend Account Card */}
          <Card className="bg-card border-border spend-accent" data-testid="spend-account-card">
            <CardHeader className="pb-2">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-success/20 flex items-center justify-center">
                  <Wallet className="w-5 h-5 text-emerald-400" />
                </div>
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Spend Account</p>
                  <CardTitle className="font-heading text-3xl tracking-tight" data-testid="spend-balance">
                    {spendAccount ? formatCurrency(spendAccount.current_balance) : '£0.00'}
                  </CardTitle>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">Available Balance</p>
            </CardContent>
          </Card>

          {/* Hold Account Card */}
          <Card className="bg-card border-border hold-accent" data-testid="hold-account-card">
            <CardHeader className="pb-2">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-destructive/20 flex items-center justify-center">
                  <PiggyBank className="w-5 h-5 text-red-400" />
                </div>
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Hold Account</p>
                  <CardTitle className="font-heading text-3xl tracking-tight" data-testid="hold-balance">
                    {holdAccount ? formatCurrency(holdAccount.current_balance) : '$0.00'}
                  </CardTitle>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">Reserved Balance</p>
            </CardContent>
          </Card>
        </div>

        {/* Account Details */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* Spend Account Details */}
          {spendAccount && (
            <Card className="bg-card border-border" data-testid="spend-account-details">
              <CardHeader>
                <CardTitle className="font-heading text-lg">Spend Account Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Account Name</span>
                  <span className="text-sm font-medium">{spendAccount.display_name}</span>
                </div>
                <Separator />
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Account Number</span>
                  <span className="text-sm font-mono" data-testid="spend-account-number">{spendAccount.account_number_masked}</span>
                </div>
                <Separator />
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Sort Code</span>
                  <span className="text-sm font-mono" data-testid="spend-sort-code">{spendAccount.sort_code_display}</span>
                </div>
                <Separator />
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Balance</span>
                  <span className="text-sm font-semibold text-emerald-400">{formatCurrency(spendAccount.current_balance)}</span>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Hold Account Details */}
          {holdAccount && (
            <Card className="bg-card border-border" data-testid="hold-account-details">
              <CardHeader>
                <CardTitle className="font-heading text-lg">Hold Account Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Account Name</span>
                  <span className="text-sm font-medium">{holdAccount.display_name}</span>
                </div>
                <Separator />
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Account Number</span>
                  <span className="text-sm font-mono" data-testid="hold-account-number">{holdAccount.account_number_masked}</span>
                </div>
                <Separator />
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Sort Code</span>
                  <span className="text-sm font-mono" data-testid="hold-sort-code">{holdAccount.sort_code_display}</span>
                </div>
                <Separator />
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Balance</span>
                  <span className="text-sm font-semibold text-red-400">{formatCurrency(holdAccount.current_balance)}</span>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Treasury Account (SQI, BVI) - Informational Section */}
        {treasuryAccount && (
          <div className="mb-8" data-testid="treasury-section">
            <div className="bg-secondary/30 border border-border rounded-lg p-5">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-accent/20 flex items-center justify-center flex-shrink-0">
                  <Building2 className="w-5 h-5 text-blue-400" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-heading text-lg font-medium" data-testid="treasury-name">
                      {treasuryAccount.display_name}
                    </h3>
                    <span className="font-heading text-xl font-semibold text-blue-400" data-testid="treasury-balance">
                      {formatCurrency(treasuryAccount.current_balance)}
                    </span>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground flex items-center gap-2">
                      <Info className="w-4 h-4 flex-shrink-0" />
                      Hold Account funds are maintained within the Treasury Account (SQI, BVI).
                    </p>
                    <p className="text-xs text-muted-foreground/70 ml-6">
                      Treasury Account banking is provided by JP Morgan.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Transactions */}
        <div className="space-y-6">
          {/* Spend Account Transactions */}
          {spendAccount && (
            <Card className="bg-card border-border" data-testid="spend-transactions">
              <CardHeader>
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <Wallet className="w-5 h-5 text-emerald-400" />
                  Spend Account Transactions
                </CardTitle>
              </CardHeader>
              <CardContent>
                {getAccountTransactions(spendAccount.id).length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow className="border-border hover:bg-transparent">
                        <TableHead className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Date</TableHead>
                        <TableHead className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Description</TableHead>
                        <TableHead className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Type</TableHead>
                        <TableHead className="text-xs font-bold uppercase tracking-wider text-muted-foreground text-right">Amount</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {getAccountTransactions(spendAccount.id).map((tx, idx) => (
                        <TableRow key={tx.id} className="border-border" data-testid={`spend-tx-row-${idx}`}>
                          <TableCell className="text-sm">{formatDate(tx.date)}</TableCell>
                          <TableCell className="text-sm font-medium">{tx.description}</TableCell>
                          <TableCell>
                            <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded ${
                              tx.debit_or_credit === 'Credit' 
                                ? 'bg-success/20 text-emerald-400' 
                                : 'bg-destructive/20 text-red-400'
                            }`}>
                              {tx.debit_or_credit === 'Credit' 
                                ? <ArrowDownRight className="w-3 h-3" /> 
                                : <ArrowUpRight className="w-3 h-3" />}
                              {tx.debit_or_credit}
                            </span>
                          </TableCell>
                          <TableCell className={`text-sm font-mono text-right ${
                            tx.debit_or_credit === 'Credit' ? 'text-emerald-400' : 'text-red-400'
                          }`}>
                            {tx.debit_or_credit === 'Credit' ? '+' : '-'}{formatCurrency(tx.amount)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-8">No transactions available</p>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </main>
    </div>
  );
};

export default UserDashboard;
